import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import axiosApiInstance from '@/api/shared/axios-instance';
import URL_FACTORY from '@/helpers/url-helper';
import { mapToEvents } from '../mappers/MapToAlineacionesProbables';
import { LEAGUES } from '@/constants/leagues';
import { currentSeasonStats } from '@/constants/site';
import { FANTASY } from '@/helpers/biwenger-helper';

export const EquipoAlineacionesContext = createContext(null);

export const useEquipoAlineaciones = () => {
    const context = useContext(EquipoAlineacionesContext);
    if (!context) {
        throw new Error('useEquipoAlineaciones debe ser usado dentro de un EquipoAlineacionesProvider');
    }
    return context;
};

export const EquipoAlineacionesProvider = ({
    children,
    data,
    equipoId,
    confirmedFantasy,
    confirmedFantasyMode,
}) => {
    const [variant, setVariant] = useState('vertical');
    const [prediction, setPrediction] = useState(null);
    const [events, setEvents] = useState(null);
    const [estadisticasPartido, setEstadisticasPartido] = useState(null);
    const [estadisticasPartidoSeleccionada, setEstadisticasPartidoSeleccionada] = useState(null);
    const [minutero, setMinutero] = useState(null);
    const [editorLineups, setEditorLineups] = useState(null);
    const [selectedEditorLineup, setSelectedEditorLineup] = useState(null);

    // Datos derivados
    const mode = data?.mode;
    const home = data?.home?.id > 0 ? data?.home : null;
    const away = data?.away?.id > 0 ? data?.away : null;
    const lineup = home?.lineup ?? away?.lineup;
    const lastFixture = home?.lastFixture ?? away?.lastFixture;
    const formation = home?.formation ?? away?.formation;

    const { id, date, league, season } = data?.fixture ?? {};
    const isGameNotStarted = useMemo(() => data?.fixture?.status === 'NS', [data]);

    // Función para agregar estadísticas a los jugadores
    const addEstadisticasPartido = (estadisticas) => {
        const statsMap = {};

        estadisticas.forEach((modo) => {
            Object.entries(modo.estadisticas).forEach(([playerId, stat]) => {
                if (!statsMap[playerId]) {
                    statsMap[playerId] = [];
                }
                statsMap[playerId].push({
                    mode: modo.mode,
                    subMode: modo.subMode,
                    estadistica: stat,
                });
            });
        });

        const applyStatsToLineup = (lineup) => {
            lineup?.forEach((player) => {
                player.estadisticas = statsMap[player.id] || [];
                player.lineupMode = data?.mode;
            });
        };

        applyStatsToLineup(data?.home?.lineup);
        applyStatsToLineup(data?.away?.lineup);
    };

    // Fetch minutero fantasy
    useEffect(() => {
        if (equipoId > 0 && season > 0) {
            const payload = {
                teams: [equipoId],
                league,
                season: currentSeasonStats,
            };

            const url = URL_FACTORY.ObtenerMinuteroFantasyData;

            axiosApiInstance
                .post(url, payload)
                .then((res) => {
                    const minuteroEquipo = res.data.equipos?.[equipoId];
                    setMinutero(minuteroEquipo);
                })
                .catch((err) => {
                    console.log(err);
                });
        }
    }, [equipoId, league, season]);

    // Fetch predicción del tiempo
    useEffect(() => {
        if (!id || !date || league !== LEAGUES.LA_LIGA || mode !== 'predict') return;

        const now = new Date();
        const inputDate = new Date(date);

        const sevenDaysAgo = new Date(now);
        sevenDaysAgo.setUTCDate(now.getUTCDate() - 7);

        if (inputDate >= sevenDaysAgo && inputDate >= now && mode === 'predict') {
            const url = URL_FACTORY.ObtenerPrediccionDelTiempo(id);
            axiosApiInstance.get(url).then((res) => {
                setPrediction(res.data);
            });
        }
    }, [id, date, league, mode]);

    // Fetch eventos del partido
    useEffect(() => {
        if (!data || isGameNotStarted) return;

        let fixtureId = null;
        let extraId = null;
        if (mode === 'last') {
            fixtureId = home?.lastFixture?.id;
            extraId = away?.lastFixture?.id;
        } else if (mode === 'confirmed') {
            fixtureId = data.fixture?.id;
        }

        if (fixtureId) {
            const url = URL_FACTORY.ObtenerPartidoFixtureEvents(fixtureId, extraId);
            axiosApiInstance.get(url).then((res) => {
                setEvents(res.data?.length > 0 ? mapToEvents(res.data) : []);
            });
        }
    }, [data, isGameNotStarted, mode, home, away]);

    // Fetch estadísticas del partido
    useEffect(() => {
        let idsPartido = [];

        if (mode === 'last') {
            idsPartido = [home?.lastFixture?.id ?? away?.lastFixture?.id];
        } else {
            idsPartido = [id];
        }

        let inputDate = new Date(date);

        if (!idsPartido || idsPartido.length === 0 || !inputDate) return;

        const now = new Date();

        if (inputDate < now) {
            const url = URL_FACTORY.ObtenerPartidoEstadisticas;

            const homePlayers =
                data?.home?.lineup?.filter((player) => player.id).map((player) => player.id) ?? [];
            const awayPlayers =
                data?.away?.lineup?.filter((player) => player.id).map((player) => player.id) ?? [];

            const players = [...homePlayers, ...awayPlayers];

            if (players.length > 0) {
                const payload = {
                    ids: idsPartido,
                    mode: confirmedFantasy,
                    subMode: confirmedFantasyMode,
                    players,
                };
                axiosApiInstance.post(url, payload).then((res) => {
                    setEstadisticasPartido(res.data?.estadisticas);
                    addEstadisticasPartido(res.data?.estadisticas);
                });
            }
        }
    }, [id, date, data, mode, home, away, confirmedFantasy, confirmedFantasyMode]);

    // Actualizar estadísticas seleccionadas según fantasy mode
    useEffect(() => {
        if (confirmedFantasy && confirmedFantasyMode && estadisticasPartido) {
            const estadisticasSeleccionadas = estadisticasPartido?.find(
                (estadistica) =>
                    estadistica.mode === confirmedFantasy && estadistica.subMode === confirmedFantasyMode
            );

            setEstadisticasPartidoSeleccionada(estadisticasSeleccionadas);
        }
    }, [confirmedFantasy, confirmedFantasyMode, estadisticasPartido]);

    // Fetch editor lineups
    useEffect(() => {
        if (!id || !equipoId) return;

        const url = URL_FACTORY.ObtenerAlineacionesEditores;
        const payload = {
            fixtureId: id,
            teamId: equipoId,
        };

        axiosApiInstance
            .post(url, payload)
            .then((res) => {
                setEditorLineups(res.data);
            })
            .catch((err) => {
                console.error('Error fetching editor lineups:', err);
            });
    }, [id, equipoId]);

    // Calcular fantasyEnabled
    const fantasyEnabled = useMemo(() => {
        return {
            isBiwengerEnabled: estadisticasPartido?.find(
                (estadistica) =>
                    estadistica.mode === FANTASY.BIWENGER &&
                    estadistica.estadisticas &&
                    Object.keys(estadistica.estadisticas).length > 0
            ),
            isFantasyLaLigaEnabled: estadisticasPartido?.find(
                (estadistica) =>
                    estadistica.mode === FANTASY.FANTASY &&
                    estadistica.estadisticas &&
                    estadistica.estadisticas
            ),
            isAnaliticaFantasyEnabled: estadisticasPartido?.find(
                (estadistica) => estadistica.mode === FANTASY.ANALITICA && estadistica.estadisticas
            ),
            isFantasyMarcaEnabled: estadisticasPartido?.find(
                (estadistica) => estadistica.mode === FANTASY.FANTASY_MARCA && estadistica.estadisticas
            ),
            isMisterFantasyEnabled: estadisticasPartido?.find(
                (estadistica) => estadistica.mode === FANTASY.MISTER_FANTASY && estadistica.estadisticas
            ),
        };
    }, [estadisticasPartido]);

    const value = {
        // Data original
        data,

        // Estados de fetching
        events,
        estadisticasPartido: estadisticasPartidoSeleccionada,
        minutero,
        prediccionTiempo: prediction,
        fantasyEnabled,
        editorLineups,

        // Estado del variant y editor seleccionado
        variant,
        setVariant,
        selectedEditorLineup,
        setSelectedEditorLineup,

        // Datos derivados
        mode,
        home,
        away,
        lineup,
        lastFixture,
        formation,
    };

    return <EquipoAlineacionesContext.Provider value={value}>{children}</EquipoAlineacionesContext.Provider>;
};
